import { useState } from "react";
import { RACES, GENDERS, CLASSES, walkerTitle, walkerSubtitle, walkerId, type Walker } from "../../shared/avatars.ts";
import { api, type User } from "../lib/api";
import type { ThemePref } from "../lib/prefs";
import { Portrait } from "./Sprite";

interface Props {
  user: User;
  walker: Walker;
  theme: ThemePref;
  reducedMotion: boolean;
  onUser: (u: User) => void;
  onTheme: (t: ThemePref) => void;
  onReducedMotion: (v: boolean) => void;
  onSignOut: () => void;
  onDeleted: () => void;
}

export default function WalkerScreen({ user, walker, theme, reducedMotion, onUser, onTheme, onReducedMotion, onSignOut, onDeleted }: Props) {
  const [name, setName] = useState(user.display_name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (patch: Parameters<typeof api.updateMe>[0]) => {
    setBusy(true);
    setError(null);
    try {
      const { user: u } = await api.updateMe(patch);
      onUser(u);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const setWalker = (patch: Partial<Walker>) => save({ avatar_id: walkerId({ ...walker, ...patch }) });
  const deleteAccount = async () => {
    if (!confirm("Delete your account and every logged activity? This cannot be undone.")) return;
    await api.deleteMe();
    onDeleted();
  };

  return (
    <div className="screen" aria-label="Choose your walker">
      <div className="screen-inner">
        <h1 className="h1">Your Walker</h1>
        <div className="sub">Choose who walks the road for you.</div>

        <div className="card walker-card">
          <Portrait walker={walker} size={116} />
          <div style={{ flex: 1, minWidth: 150 }}>
            <div className="walker-title">{walkerTitle(walker)}</div>
            <div className="walker-sub">{walkerSubtitle(walker)}</div>
            <div className="walker-marker"><Portrait walker={walker} size={48} ring /><span>your marker on the map</span></div>
          </div>
        </div>

        <div className="overline field-label">Race</div>
        <div className="race-row" role="radiogroup" aria-label="Race">
          {RACES.map(([id, label]) => (
            <button key={id} role="radio" aria-checked={walker.race === id} className={walker.race === id ? "race-btn sel" : "race-btn"} disabled={busy} onClick={() => setWalker({ race: id })}>
              <Portrait walker={{ ...walker, race: id }} size={40} />
              {label}
            </button>
          ))}
        </div>

        <div className="overline field-label" style={{ marginTop: 18 }}>Gender</div>
        <div className="seg" role="radiogroup" aria-label="Gender">
          {GENDERS.map(([id, label]) => (
            <button key={id} role="radio" aria-checked={walker.gender === id} className={walker.gender === id ? "seg-btn sel" : "seg-btn"} disabled={busy} onClick={() => setWalker({ gender: id })}>{label}</button>
          ))}
        </div>

        <div className="overline field-label" style={{ marginTop: 18 }}>Class</div>
        <div className="seg" role="radiogroup" aria-label="Class">
          {CLASSES.map(([id, label]) => (
            <button key={id} role="radio" aria-checked={walker.cls === id} className={walker.cls === id ? "seg-btn sel" : "seg-btn"} style={{ padding: "0 14px" }} disabled={busy} onClick={() => setWalker({ cls: id })}>{label}</button>
          ))}
        </div>

        <div className="overline field-label" style={{ marginTop: 26 }}>Display name</div>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== user.display_name && save({ display_name: name.trim() })} aria-label="Display name" />

        <div className="overline field-label">Units</div>
        <div className="seg" role="radiogroup" aria-label="Units">
          {([["mi", "Miles"], ["km", "Kilometres"]] as const).map(([id, label]) => (
            <button key={id} role="radio" aria-checked={user.units === id} className={user.units === id ? "seg-btn sel" : "seg-btn"} style={{ padding: "0 20px", fontSize: 15 }} disabled={busy} onClick={() => save({ units: id })}>{label}</button>
          ))}
        </div>

        <div className="overline field-label">Theme</div>
        <div className="seg" role="radiogroup" aria-label="Theme">
          {([["system", "Match device"], ["light", "Daylight"], ["dark", "Candlelight"]] as const).map(([id, label]) => (
            <button key={id} role="radio" aria-checked={theme === id} className={theme === id ? "seg-btn sel" : "seg-btn"} style={{ padding: "0 20px", fontSize: 15 }} onClick={() => onTheme(id)}>{label}</button>
          ))}
        </div>

        <button className="switch-btn" role="switch" aria-checked={reducedMotion} onClick={() => onReducedMotion(!reducedMotion)}>
          <div className={reducedMotion ? "switch on" : "switch"}><i /></div>
          <span>Reduce motion</span>
        </button>

        <button className="switch-btn" role="switch" aria-checked={user.show_on_leaderboard} onClick={() => save({ show_on_leaderboard: !user.show_on_leaderboard })} disabled={busy}>
          <div className={user.show_on_leaderboard ? "switch on" : "switch"}><i /></div>
          <span>Show me on the leaderboard</span>
        </button>

        {error && <p className="error">{error}</p>}

        <div className="section-end">
          <button className="btn-secondary" onClick={onSignOut}>Sign out</button>
          <button className="btn-danger" onClick={deleteAccount}>Delete account and all journey data</button>
        </div>
      </div>
    </div>
  );
}
